import { engineBandForLoss, tacticalStatusForLoss } from "../chess/analysisCoordinator.ts";
import { positionKey } from "../chess/positionKey.ts";
import { lookupLesson, type LessonLookup } from "./repository.ts";
import type { LessonCourse, LessonMove, MoveAssessment } from "./schema.ts";

export type LegacyAlternative = {
  san: string;
  label: string;
  why: string;
  role?: LessonMove["role"] | "playable";
  concepts?: string[];
};

const FLANK_PAWN = /^[ah][45]$/;

export function sanitizeRecommendedSan(san: string | null | undefined): string | null {
  if (!san) return null;
  const clean = san.replace(/[+#]/g, "");
  if (FLANK_PAWN.test(clean)) return null;
  return san;
}

export function partitionCoachAlternatives(
  alternatives: Array<{ san: string; why: string; role?: string; label?: string }>
): {
  playable: typeof alternatives;
  sideline: typeof alternatives;
  avoid: typeof alternatives;
} {
  const playable: typeof alternatives = [];
  const sideline: typeof alternatives = [];
  const avoid: typeof alternatives = [];
  for (const alt of alternatives) {
    const role = alt.role || alt.label || "";
    if (role === "mistake") {
      avoid.push(alt);
      continue;
    }
    if (role === "sideline") {
      sideline.push(alt);
      continue;
    }
    // Unvetted flank-pawn moves are left out, but an authored option the
    // course itself calls acceptable is always listed.
    if (role !== "playable" && FLANK_PAWN.test(alt.san.replace(/[+#]/g, ""))) continue;
    playable.push(alt);
  }
  return { playable, sideline, avoid };
}

export type TheoryResolution = {
  source: "graph" | "legacy";
  inGraph: boolean;
  pastSlice: boolean;
  mainlineSan: string | null;
  alternatives: LegacyAlternative[];
  lookup: LessonLookup | null;
  recommendedSan: string | null;
};

function edgeToAlternative(move: LessonMove): LegacyAlternative {
  return {
    san: move.san,
    label: move.role,
    role: move.role,
    why: move.explanation,
    concepts: move.concepts,
  };
}

export function resolveTheory(args: {
  useGraph: boolean;
  course: LessonCourse | null;
  fenBefore: string;
  played: { san?: string; uci?: string };
  ply: number;
  legacyMainline: string[];
  legacyAlternatives?: Record<number, LegacyAlternative[]>;
}): TheoryResolution {
  const legacyMainlineSan = args.legacyMainline[args.ply - 1] || null;
  const legacy: TheoryResolution = {
    source: "legacy",
    inGraph: false,
    pastSlice: false,
    mainlineSan: legacyMainlineSan,
    alternatives: args.legacyAlternatives?.[args.ply] || [],
    lookup: null,
    recommendedSan: legacyMainlineSan,
  };

  if (!args.useGraph || !args.course) return legacy;

  const lookup = lookupLesson(args.course, args.fenBefore, args.played);
  if (!lookup.inGraph) {
    return {
      source: "graph",
      inGraph: false,
      pastSlice: false,
      mainlineSan: null,
      alternatives: [],
      lookup,
      recommendedSan: null,
    };
  }

  if (lookup.pastSlice) {
    return {
      source: "graph",
      inGraph: true,
      pastSlice: true,
      mainlineSan: null,
      alternatives: [],
      lookup,
      recommendedSan: null,
    };
  }

  const repertoireSan = lookup.repertoireMove?.san || null;
  const playedRole = lookup.edge?.role;
  const playedIsCourse =
    playedRole === "repertoire" || playedRole === "mainline" || playedRole === "transposition";

  return {
    source: "graph",
    inGraph: true,
    pastSlice: false,
    mainlineSan: playedIsCourse && lookup.edge ? lookup.edge.san : repertoireSan,
    alternatives: (lookup.node?.moves || [])
      .filter((m) => m.role !== "repertoire" && m.role !== "mainline")
      .map(edgeToAlternative),
    lookup,
    recommendedSan: playedIsCourse ? null : repertoireSan,
  };
}

export function replyFromGraph(course: LessonCourse, fen: string): string | null {
  const node = course.nodes[positionKey(fen)];
  if (!node || node.handoff) return null;
  const reply = node.moves.find((m) => m.role === "repertoire" || m.role === "mainline");
  return reply?.san || null;
}

function namedDestination(course: LessonCourse, move: LessonMove): boolean {
  return Boolean(course.nodes[move.nextPositionKey]?.variationName);
}

export type NamedReply = { san: string; name: string; note: string };

/** Named replies from this position, aside from the course's own main line. */
export function namedRepliesAt(course: LessonCourse, fen: string): NamedReply[] {
  const node = course.nodes[positionKey(fen)];
  if (!node || node.handoff) return [];
  const replies: NamedReply[] = [];
  for (const move of node.moves) {
    if (move.role === "repertoire" || move.role === "mainline") continue;
    const dest = course.nodes[move.nextPositionKey];
    const name = dest?.variationName || move.variationName;
    if (!name) continue;
    const source = (move.explanation || dest?.opponentIntent || "").trim();
    const note = source.split(/(?<=[.!?])\s+/)[0] || "";
    replies.push({ san: move.san, name, note });
  }
  return replies;
}

function branchRank(
  key: string,
  prefer?: { dueKeys?: Set<string>; weakKeys?: Set<string>; seenKeys?: Set<string> }
): number {
  if (prefer?.dueKeys?.has(key)) return 0;
  if (prefer?.weakKeys?.has(key)) return 1;
  if (prefer?.seenKeys && !prefer.seenKeys.has(key)) return 2;
  return 3;
}

/**
 * Prefer a named off-mainline reply (Black branches), then continue the current
 * named variation, then BFS toward a named node, else the graph mainline.
 */
export function replyFromNamedBranch(
  course: LessonCourse,
  fen: string,
  prefer?: { dueKeys?: Set<string>; weakKeys?: Set<string>; seenKeys?: Set<string> }
): string | null {
  const node = course.nodes[positionKey(fen)];
  if (!node || node.handoff) return replyFromGraph(course, fen);

  const namedNow = node.moves
    .filter((m) => m.role !== "repertoire" && m.role !== "mainline" && namedDestination(course, m))
    .sort((a, b) => branchRank(a.nextPositionKey, prefer) - branchRank(b.nextPositionKey, prefer));
  if (namedNow[0]) return namedNow[0].san;

  if (node.variationName) {
    const cont =
      node.moves.find(
        (m) =>
          m.role === "mainline" ||
          m.role === "repertoire" ||
          m.role === "sideline" ||
          m.role === "transposition"
      ) || node.moves[0];
    return cont?.san || replyFromGraph(course, fen);
  }

  const seen = new Set<string>([node.positionKey]);
  const queue: { key: string; firstSan: string }[] = [];
  for (const move of node.moves) {
    queue.push({ key: move.nextPositionKey, firstSan: move.san });
  }
  while (queue.length) {
    const cur = queue.shift()!;
    if (seen.has(cur.key)) continue;
    seen.add(cur.key);
    const next = course.nodes[cur.key];
    if (!next) continue;
    if (next.variationName) return cur.firstSan;
    for (const move of next.moves) {
      if (!seen.has(move.nextPositionKey)) {
        queue.push({ key: move.nextPositionKey, firstSan: cur.firstSan });
      }
    }
  }

  return replyFromGraph(course, fen);
}

function bookFromLookup(
  lookup: LessonLookup | null,
  inGraph: boolean,
  pastSlice: boolean
): MoveAssessment["book"] {
  if (pastSlice) return { status: "unknown" };
  if (!inGraph || !lookup?.node) return { status: "novel" };
  const edge = lookup.edge;
  if (!edge) return { status: "novel" };
  if (edge.role === "repertoire" || edge.role === "mainline") {
    return { status: "repertoire", variation: lookup.node.variationName };
  }
  if (edge.role === "transposition") {
    return { status: "transposition", variation: lookup.node.variationName };
  }
  if (edge.role === "mistake") return { status: "warned", variation: lookup.node.variationName };
  return { status: "known", variation: lookup.node.variationName };
}

function planFromLookup(
  lookup: LessonLookup | null,
  inGraph: boolean,
  pastSlice: boolean
): MoveAssessment["plan"] {
  if (pastSlice) return { fit: "unknown" };
  if (!inGraph) {
    return { fit: "unknown", explanation: "This position is not in the course graph." };
  }
  const edge = lookup?.edge;
  if (!edge && !lookup?.repertoireMove) {
    // The course's prepared moves have ended here, so there is no plan to fit.
    return { fit: "unknown", explanation: "The course has no prepared move in this position." };
  }
  if (!edge) {
    // A move the course does not cover is a different move order, not
    // necessarily a move against the plan.
    return {
      fit: "neutral",
      explanation: "This move is not part of the course in this position.",
    };
  }
  if (edge.role === "repertoire" || edge.role === "mainline" || edge.role === "transposition") {
    return { fit: "advances", explanation: edge.explanation };
  }
  if (edge.role === "sideline") {
    return { fit: "neutral", explanation: edge.explanation };
  }
  return { fit: "conflicts", explanation: edge.explanation };
}

export function assessMove(args: {
  lookup: LessonLookup | null;
  inGraph: boolean;
  pastSlice: boolean;
  cpLoss: number | null;
  depth?: number;
  playedSan: string;
  mateAfter?: number | null;
  mateBefore?: number | null;
  materialDrop?: number | null;
}): MoveAssessment {
  const book = bookFromLookup(args.lookup, args.inGraph, args.pastSlice);
  const plan = planFromLookup(args.lookup, args.inGraph, args.pastSlice);
  const loss = args.cpLoss == null ? undefined : args.cpLoss;
  const recommended =
    book.status === "repertoire" || book.status === "transposition"
      ? null
      : sanitizeRecommendedSan(args.lookup?.repertoireMove?.san ?? null);
  return {
    tactical: {
      status: tacticalStatusForLoss(loss, args.mateAfter, args.mateBefore, args.materialDrop),
      ...(loss != null ? { cpLoss: loss } : {}),
    },
    engine: {
      band: engineBandForLoss(loss),
      ...(args.depth != null ? { depth: args.depth } : {}),
    },
    book,
    plan,
    recommendedSan: recommended,
  };
}

export function formatAssessmentAxes(assessment: MoveAssessment): string {
  return [
    `Book ${assessment.book.status}`,
    `Engine ${assessment.engine.band === "unknown" ? "not checked" : assessment.engine.band}`,
    `Plan ${assessment.plan.fit}`,
    `Risk ${assessment.tactical.status}`,
  ].join(" · ");
}
